import React, { useState, useEffect, useCallback } from "react";
import { Send, Loader2, Megaphone, Users, CloudRain, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AUDIENCES = [
  { value: "all", label: "All Players" },
  { value: "7-9", label: "Ages 7–9" },
  { value: "10-12", label: "Ages 10–12" },
  { value: "13-15", label: "Ages 13–15" },
];

const TEMPLATES = [
  {
    icon: CloudRain,
    label: "Weather Cancellation",
    subject: "Training Cancelled — Weather",
    message:
      "Due to weather conditions, today's training session is cancelled. The safety of our players comes first. We'll follow up with a make-up plan as soon as possible. Thank you for your understanding!\n\n— ARD Midsouth Performance",
  },
  {
    icon: Clock,
    label: "Time Change",
    subject: "Training Time Change",
    message:
      "Please note: the time for an upcoming training session has changed. Kindly check the new schedule below and plan accordingly.\n\nNew time: [ADD TIME HERE]\nLocation: Northpoint Christian School\n\nThank you!\n— ARD Midsouth Performance",
  },
];

const inputCls =
  "w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-neon-green focus:ring-1 focus:ring-neon-green";
const labelCls = "block font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80 mb-2";

export function AnnouncementsPanel() {
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadRecipients = useCallback(async (aud) => {
    setCount(null);
    try {
      const { data } = await api.get(`/announcements/recipients?audience=${aud}`);
      setCount(data.count);
    } catch (e) {
      setCount(0);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements");
      setHistory(data);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadRecipients(audience);
  }, [audience, loadRecipients]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setMessage(t.message);
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      toast("Add a subject and a message first.");
      return;
    }
    if (!count) {
      toast("No parents match this audience yet.");
      return;
    }
    if (!window.confirm(`Send this announcement to ${count} parent${count === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/announcements", { subject, message, audience });
      toast(`Sent to ${data.sent_count} of ${data.recipient_count} parents.`);
      setSubject("");
      setMessage("");
      loadHistory();
    } catch (e) {
      toast("Could not send announcement.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="announcements-panel" className="grid gap-8 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <div className="mb-6 flex items-center gap-3">
          <Megaphone size={22} strokeWidth={2.5} className="text-neon-green" />
          <div>
            <h2 className="font-heading text-2xl uppercase leading-none text-white">Notify Parents</h2>
            <p className="font-body text-xs uppercase tracking-widest text-white/40">Time changes & weather cancellations</p>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-3">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.label} onClick={() => applyTemplate(t)} data-testid={`template-${t.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-body text-xs font-bold uppercase tracking-widest text-white/70 hover:border-neon-green hover:text-neon-green">
                <Icon size={14} strokeWidth={2.5} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="space-y-5 border border-white/10 bg-surface p-6">
          <div>
            <label className={labelCls}>Send To</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {AUDIENCES.map((a) => (
                <button
                  key={a.value}
                  onClick={() => setAudience(a.value)}
                  data-testid={`audience-${a.value}`}
                  className={`border py-3 font-body text-xs font-bold uppercase tracking-widest transition-colors ${
                    audience === a.value ? "border-neon-green bg-neon-green text-black" : "border-white/15 bg-zinc-900 text-white/70 hover:border-neon-green/60"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 font-body text-sm text-white/60">
              <Users size={16} strokeWidth={2.5} className="text-neon-green" />
              {count === null ? "Counting recipients…" : <span data-testid="recipient-count"><span className="font-bold text-white">{count}</span> parent{count === 1 ? "" : "s"} will receive this</span>}
            </div>
          </div>

          <div>
            <label className={labelCls}>Subject</label>
            <input data-testid="announcement-subject" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Training Cancelled — Weather" />
          </div>

          <div>
            <label className={labelCls}>Message</label>
            <textarea data-testid="announcement-message" rows={7} className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Write your message to parents…" />
          </div>

          <button
            onClick={send}
            disabled={sending}
            data-testid="send-announcement-btn"
            className="flex w-full items-center justify-center gap-2 bg-neon-green py-4 font-heading text-lg uppercase tracking-widest text-black transition-opacity disabled:opacity-60"
          >
            {sending ? <Loader2 className="animate-spin" size={22} /> : <><Send size={18} strokeWidth={2.5} /> Send Announcement</>}
          </button>
        </div>
      </div>

      <div className="lg:col-span-2">
        <h3 className="mb-4 font-heading text-xl uppercase text-white">Sent History</h3>
        <div className="space-y-3" data-testid="announcement-history">
          {history.length === 0 && <p className="font-body text-sm text-white/40">No announcements sent yet.</p>}
          {history.map((h) => (
            <div key={h.id} className="border border-white/10 bg-surface p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="font-heading text-base uppercase leading-tight text-white">{h.subject}</div>
                <span className="shrink-0 border border-neon-green/50 px-2 py-0.5 font-body text-[10px] font-bold uppercase text-neon-green">
                  {h.audience === "all" ? "All" : h.audience}
                </span>
              </div>
              <p className="mt-1 line-clamp-2 font-body text-xs text-white/50">{h.message}</p>
              <div className="mt-2 font-body text-[11px] uppercase tracking-widest text-white/30">
                {h.sent_count}/{h.recipient_count} sent • {new Date(h.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
